/**
 * Concern card with clickable evidence citation popovers.
 */

import { AlertTriangle, FileText } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";

// ---------------------------------------------------------------------------
// Concern Card
// ---------------------------------------------------------------------------

/**
 * Boundary shape for a concern's evidence citation. The backend query
 * (`getSummarizationTaskDetail`) declares `evidenceCitations` as
 * `v.array(v.any())` — the citation schema varies across analysis agents —
 * so we type only the fields this component reads.
 */
interface EvidenceCitation {
  documentId?: string;
  chunkId?: string;
  excerpt?: string;
}

interface Concern {
  description: string;
  severity: string;
  evidenceCitations?: EvidenceCitation[];
}

export function ConcernCard({ concern }: { concern: Concern }) {
  const severityConfig = {
    high: {
      border: "border-destructive/40 bg-destructive/5",
      icon: "text-destructive",
      badge: "destructive" as const,
    },
    medium: {
      border: "border-amber-500/40 bg-amber-50/50 dark:bg-amber-950/20",
      icon: "text-amber-500",
      badge: "default" as const,
    },
    low: {
      border: "border-muted bg-muted/20",
      icon: "text-muted-foreground",
      badge: "secondary" as const,
    },
  };
  const config =
    severityConfig[concern.severity as keyof typeof severityConfig] ??
    severityConfig.low;

  return (
    <div className={cn("rounded-lg border p-4", config.border)}>
      <div className="flex gap-3">
        <AlertTriangle className={cn("mt-0.5 h-4 w-4 shrink-0", config.icon)} />
        <div className="min-w-0 flex-1">
          <p className="text-sm leading-relaxed">{concern.description}</p>

          <div className="mt-3 flex items-center gap-2">
            <Badge
              color={
                concern.severity === "high"
                  ? "red"
                  : concern.severity === "medium"
                    ? "amber"
                    : "blue"
              }
              variant="soft"
              size="sm"
            >
              {concern.severity.charAt(0).toUpperCase() +
                concern.severity.slice(1)}
            </Badge>

            {concern.evidenceCitations &&
              concern.evidenceCitations.length > 0 && (
                <div className="flex items-center gap-1">
                  {concern.evidenceCitations.map((citation, j) => (
                    <CitationPopover
                      key={j}
                      citation={citation}
                      index={j + 1}
                    />
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Citation Popover
// ---------------------------------------------------------------------------

function CitationPopover({
  citation,
  index,
}: {
  citation: EvidenceCitation;
  index: number;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary/5 px-1.5 py-0.5 text-xs text-primary transition-colors hover:bg-primary/10 hover:text-primary/80 hover:underline">
          <FileText className="h-3 w-3" />
          <span>Source {index}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="bottom">
        <div className="space-y-2 p-3">
          <div className="flex items-start gap-2">
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              {citation.documentId ? (
                <p className="text-sm font-medium leading-tight">
                  Document {citation.documentId.slice(-8)}
                </p>
              ) : (
                <p className="text-sm font-medium leading-tight text-muted-foreground">
                  Source document
                </p>
              )}
              {citation.chunkId && (
                <p className="text-xs text-muted-foreground">
                  Passage: {citation.chunkId.slice(-8)}
                </p>
              )}
            </div>
          </div>

          {citation.excerpt ? (
            <div className="rounded-md bg-muted/50 p-2.5">
              <p className="text-xs italic leading-relaxed text-muted-foreground">
                &ldquo;{citation.excerpt}&rdquo;
              </p>
            </div>
          ) : (
            <p className="text-xs italic text-muted-foreground">
              No excerpt available for this citation.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
